/**
 * FS App Source Identification
 *
 * Tags FS sessions with the app source for filtering/segmentation.
 *
 * 1. Sets a page-level `appSource` property on every page navigation, making
 *    all sessions filterable/segmentable in FS.
 * 2. Fires a one-time "App Source Identified" event per app load for
 *    event-stream analysis.
 */
const APP_SOURCE = 'borrower-portal'
const MAX_RETRY_ATTEMPTS = 30
const RETRY_INTERVAL_MS = 500

export default defineNuxtPlugin((nuxtApp) => {
  let eventFired = false
  let translationEventHandled = false

  function getFsInstance() {
    const fsAvailable = window?._fs_namespace && typeof window[window._fs_namespace] === 'function'
    return fsAvailable ? window[window._fs_namespace] : null
  }

  function observePageTranslation() {
    const html = document.documentElement

    function getTranslationState() {
      const isRtl = html.classList.contains('translated-rtl')
      if (!isRtl && !html.classList.contains('translated-ltr')) return null
      const lang = html.lang
      if (!lang) return null
      return { targetLanguage: lang, direction: isRtl ? 'rtl' : 'ltr' }
    }

    function fireTranslationEvent(state) {
      const fsInstance = getFsInstance()
      if (!fsInstance) return false
      try {
        fsInstance('trackEvent', { name: 'Page Translated', properties: state })
        return true
      } catch (e) {
        console.warn('Failed to fire Page Translated event', e)
        return false
      }
    }

    // Handle auto-translate (page already translated before observer starts)
    const initialState = getTranslationState()
    if (initialState) {
      fireTranslationEvent(initialState)
      return
    }

    const observer = new MutationObserver(() => {
      const state = getTranslationState()
      if (state && fireTranslationEvent(state)) {
        observer.disconnect()
      }
    })

    observer.observe(html, { attributes: true, attributeFilter: ['class', 'lang'] })

    // Re-check after attaching to close any gap between the initial check and observe()
    const state = getTranslationState()
    if (state && fireTranslationEvent(state)) {
      observer.disconnect()
    }
  }

  function setAppSource(fsInstance) {
    try {
      fsInstance('setProperties', {
        type: 'page',
        properties: {
          appSource: APP_SOURCE,
        }
      })
    } catch (e) {
      console.warn('Failed to set appSource property on FS', e)
    }

    if (!eventFired) {
      try {
        fsInstance('trackEvent', {
          name: 'App Source Identified',
          properties: {
            appSource: APP_SOURCE,
          }
        })
        eventFired = true
      } catch (e) {
        console.warn('Failed to fire App Source Identified event', e)
      }
    }

    if (!translationEventHandled) {
      translationEventHandled = true
      observePageTranslation()
    }
  }

  function waitForFs() {
    let attempts = 0
    const interval = setInterval(() => {
      attempts++
      const fsInstance = getFsInstance()
      if (fsInstance) {
        clearInterval(interval)
        setAppSource(fsInstance)
      } else if (attempts >= MAX_RETRY_ATTEMPTS) {
        clearInterval(interval)
      }
    }, RETRY_INTERVAL_MS)
  }

  nuxtApp.hook('page:finish', () => {
    const fsInstance = getFsInstance()
    if (fsInstance) {
      setAppSource(fsInstance)
    } else if (!eventFired) {
      // First page load — FS loads via GTM and may not be ready yet
      waitForFs()
    }
  })
})

